import axios from 'axios';
import { NavigationGuardReturn, RouteLocationNormalized, RouteLocationNormalizedLoaded } from 'vue-router';
import { ApiResponse, Page } from '@types';

const usePageStore = defineStore('page', () => {
    // Data
    const page = ref<Page | null>(null);
    const pageTo = ref<string | null>(null);
    const pageToParams = ref<string | null>(null);

    // Methods
    const beforePageEnter = async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded): Promise<NavigationGuardReturn> => {
        const params = JSON.stringify(to.params);

        if ((pageTo.value === to.name) && (pageToParams.value === params)) {
            return true;
        }

        pageTo.value = to.name as string;
        pageToParams.value = params;

        return new Promise((resolve) => {
            getPageData(to)
                .then(response => {
                    page.value = response.data.data;

                    resolve(true);
                })
                .catch(error => {
                    if ((error.response.status === 400) || (error.response.status === 500)) {
                        resolve({name: 'system-error'});
                    } else {
                        resolve({name: 'page-not-found'});
                    }
                })
                .finally(() => {
                    updatePageSeo(page.value);
                });
        });
    };

    const getPageData = (to: RouteLocationNormalized): Promise<ApiResponse<Page>> => {
        const slug = to.params.slug ?? to.meta.slug;

        if (to.meta.type === 'article') {
            return axios.get(`/api/article/${slug}`);
        }

        return axios.get(slug ? `/api/page/${slug}` : '/api/page');
    };

    const updatePageSeo = async (page: Page | null): Promise<void> => {
        let suffix: string = '';
        let title: string = import.meta.env.VITE_APP_NAME;
        let description: string = '';
        let keywords: string = '';
        let ogTitle: string = import.meta.env.VITE_APP_NAME;
        let ogDescription: string = '';
        let imageUrl: string = '';
        let imageWidth: string = '';
        let imageHeight: string = '';

        if (page && page.seo) {
            title = (page.seo.title || page.title) + ' | ' + import.meta.env.VITE_APP_NAME;
            description = page.seo.description || '';
            keywords = page.seo.keywords || '';
            ogTitle = (page.seo.og_title || page.seo.title || page.title) + ' | ' + import.meta.env.VITE_APP_NAME;
            ogDescription = page.seo.og_description || page.seo.description || '';

            if (page.seo.og_image) {
                imageUrl = page.seo.og_image.src;
                imageWidth = String(page.seo.og_image.width);
                imageHeight = String(page.seo.og_image.height);
            }
        } else if (page) {
            title = page.title + ' | ' + import.meta.env.VITE_APP_NAME;
            description = page.title + ' | ' + import.meta.env.VITE_APP_NAME;
        }

        if (import.meta.env.VITE_APP_ENV !== 'production') {
            suffix = (import.meta.env.VITE_APP_ENV === 'development') ? 'DEV: ' : 'LOCAL: ';
        }

        document.title = suffix + title;
        (document.querySelector('meta[name="description"]') as HTMLMetaElement).content = description;
        (document.querySelector('meta[name="keywords"]') as HTMLMetaElement).content = keywords;
        (document.querySelector('meta[property="og:url"]') as HTMLMetaElement).content = window.location.href;
        (document.querySelector('meta[property="og:title"]') as HTMLMetaElement).content = suffix + ogTitle;
        (document.querySelector('meta[property="og:description"]') as HTMLMetaElement).content = ogDescription;
        (document.querySelector('meta[property="og:image:url"]') as HTMLMetaElement).content = imageUrl;
        (document.querySelector('meta[property="og:image:width"]') as HTMLMetaElement).content = imageWidth ?? '';
        (document.querySelector('meta[property="og:image:height"]') as HTMLMetaElement).content = imageHeight ?? '';
    };

    const reset = (): void => {
        page.value = null;
        pageTo.value = null;
        pageToParams.value = null;

        void updatePageSeo(page.value);
    };

    return {
        page,
        pageTo,
        pageToParams,
        beforePageEnter,
        reset,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePageStore, import.meta.hot));
}

export default usePageStore;
